<template>
	<Layout>
		<PageHeader :title="title" :items="items" />
		<div class="row">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
						<div class="row mb-md-2">
							<div class="col-sm-12 col-md-3">
								<div class="input-group">
									<b-form-input v-model="filter" @change="searchListing" :placeholder="$t('label.search')"></b-form-input>
									<div class="input-group-append"><span class="input-group-text"><i class="mdi mdi-magnify"></i></span></div>
								</div>
							</div>
							<div class="col-sm-12 col-md-3">
								<multiselect
									v-model="isActive"
									track-by="value"
									label="text"
									:options="statusDdl"
									:searchable="true"
									:allow-empty="false"
									:placeholder="$t('message.pleaseSelectX', [$t('module.status')])"
									@input="searchListing"
								/>
							</div>
							<div class="col-sm-12 col-md-6 text-right">
								<router-link v-if="createPermission" tag="a" to="/supplier/create" class="btn btn-info w-md waves-effect waves-light">
									{{ $t("label.createX", [$t("module.supplier")]) }}
								</router-link>
							</div>
						</div>
						<div v-if="readPermission" class="table-responsive mb-0">
							<b-table v-if="status.list" :items="tableRows" :fields="fields" responsive="sm" :per-page="perPage">
								<template #cell(isActive)="data">
									<span>{{ data.item.isActiveText }}</span>
								</template>
								<template #cell(action)="data">
									<a
										v-if="updatePermission"
										href="javascript:void(0);"
										class="text-info"
										v-b-tooltip.hover
										:title="$t('label.editX', [$t('module.supplier')])"
										@click="toDetailsPage(data.item.uuid)"
									>
										<i class="mdi mdi-pencil font-size-18"></i>
									</a>
									<a
										v-if="updateStatusPermission"
										href="javascript:void(0);"
										class="text-info ml-1"
										v-b-tooltip.hover
										:title="data.item.isActive == 0 ? $t('label.restoreX', []) : $t('label.deleteX', [])"
										@click="deleteConfirmation(data.item)"
									>
										<i :class="`mdi font-size-18 mdi-delete-${data.item.isActive == 0 ? 'restore' : 'outline'}`"></i>
									</a>
								</template>
							</b-table>
							<div v-else class="d-flex justify-content-center align-items-center p-5 m-5">
								<b-spinner class="m-5" variant="info" role="status"></b-spinner>
							</div>
							<b-alert v-if="tableRows.length == 0 && status.list" show variant="warning" class="mt-0 text-center">
								<i class="mdi mdi-alert-circle-outline"></i> {{ $t("message.noResultsFound") }}
							</b-alert>
							<div class="row">
								<div class="col">
									<div class="dataTables_paginate paging_simple_numbers float-right">
										<ul class="pagination pagination-rounded mb-0">
											<b-pagination v-model="currentPage" :total-rows="totalPage" :per-page="perPage" @change="pageNavigation"></b-pagination>
										</ul>
									</div>
								</div>
							</div>
						</div>
						<div v-else class="text-center">
							{{ $t("message.noPermissionMessage") }}
						</div>
					</div>
				</div>
			</div>
		</div>
		<b-modal id="supplier-delete" :title="$t('label.confirmation')" @hidden="closeForm" hide-header-close>
			<p>{{ supplier.isActive == 0 ? $t("message.restoreConfirmation") : $t("message.deleteConfirmation") }}</p>
			<template #modal-footer>
				<b-button variant="danger" @click="$bvModal.hide('supplier-delete')">{{ $t("label.cancel") }}</b-button>
				<b-button variant="info" @click="requestDelete" :disabled="!status.save">
					{{ supplier.isActive == 0 ? $t("label.restoreX", []) : $t("label.deleteX", []) }}
				</b-button>
			</template>
		</b-modal>
	</Layout>
</template>
<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import Multiselect from "vue-multiselect";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { SHARED, SESSION } from "@/constants";
import { sessionHelper, ddlHelper } from "@/utils";
import { GLOBAL_FILTER_MODULE } from "@/state/modules/globalFilter";
import { SUPPLIER_MODULE } from "@/state/modules/supplier";
import { supplierMethods } from "@/state/helpers";

export default {
	name: "SupplierListing",
	components: { Layout, PageHeader, Multiselect },
	page: {
		title: i18n.t("label.listingX", [i18n.t("module.supplier")]),
	},
	beforeRouteLeave(to, from, next) {
		if (to.name != "Supplier-update" && to.name != "Supplier-create") {
			sessionHelper.clearSearch(SESSION.SUPPLIER_SEARCH);
		}
		next();
	},
	async created() {
		this.validPermissions();
		this.initSearchCriteria();
	},
	data() {
		return {
			title: i18n.t("label.listingX", [i18n.t("module.supplier")]),
			items: [
				{
					text: i18n.t("module.supplier"),
					active: true,
				},
			],
			currentPage: 1,
			perPage: SHARED.DEFAULT_PAGE_SIZE,
			filter: "",
			statusDdl: ddlHelper.getActivationStatus(true),
			isActive: ddlHelper.getActivationStatus(true)[1],
			supplier: {},
			fields: [
				{ key: "name", sortable: true, label: i18n.t("label.name") },
				{ key: "code", sortable: true, label: i18n.t("label.code") },
				{ key: "email", sortable: true, label: i18n.t("label.email") },
				{ key: "phone", sortable: true, label: i18n.t("label.phone") },
				{ key: "isActive", label: i18n.t("label.status") },
				{ key: "createdAt", sortable: true, label: i18n.t("label.createdAt") },
				{ key: "action", label: i18n.t("label.action") },
			],
			searchCriteria: {},
			createPermission: false,
			readPermission: false,
			updatePermission: false,
			updateStatusPermission: false,
		};
	},
	computed: {
		...mapState(SUPPLIER_MODULE, ["status"]),
		...mapState(GLOBAL_FILTER_MODULE, ["reload"]),
		tableRows() {
			return this.$store.state.supplier.list.data;
		},
		totalPage() {
			return this.$store.state.supplier.list.pagination.total || 0;
		},
	},
	watch: {
		reload() {
			this.initSearchCriteria();
		},
	},
	methods: {
		...supplierMethods,
		validPermissions() {
			let permissions = this.getPermissionCodes();
			this.createPermission = permissions.indexOf("SUPPLIER_UC") > -1;
			this.readPermission = permissions.indexOf("SUPPLIER_UR") > -1;
			this.updatePermission = permissions.indexOf("SUPPLIER_UU") > -1;
			this.updateStatusPermission = permissions.indexOf("SUPPLIER_UUS") > -1;
		},
		getPermissionCodes() {
			let permissions = sessionHelper.getPermissions();
			if (!permissions) return [];
			return JSON.parse(permissions).map((permission) => permission.code);
		},
		initSearchCriteria() {
			let isDefault = true;
			if (sessionHelper.hasSearchCriteria(SESSION.SUPPLIER_SEARCH)) {
				isDefault = false;
				this.searchCriteria = sessionHelper.retrieveSearch(SESSION.SUPPLIER_SEARCH);
				this.currentPage = this.searchCriteria.pageNumber;
			}
			if (isDefault) {
				this.searchCriteria = {
					pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
					pageSize: SHARED.DEFAULT_PAGE_SIZE,
					isActive: this.isActive.value,
					searchValue: "",
					searchArray: [],
				};
			}
			if (this.readPermission) this.getSupplierList(this.searchCriteria);
		},
		getSupplierList(filter) {
			this.getAllSuppliers({ filter });
			this.searchCriteria = filter;
			sessionHelper.storeSearch(SESSION.SUPPLIER_SEARCH, filter);
		},
		searchListing() {
			this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
			this.searchCriteria.searchArray = [];
			this.searchCriteria.searchArray.push(this.filter);
			this.searchCriteria.isActive = this.isActive.value;
			if (this.filter == "" || this.filter == null) {
				this.searchCriteria.pageNumber = 1;
				this.currentPage = 1;
			}
			this.getSupplierList(this.searchCriteria);
		},
		pageNavigation(pageNumber) {
			this.searchCriteria.pageNumber = pageNumber;
			this.getSupplierList(this.searchCriteria);
		},
		toDetailsPage(id) {
			let filter = sessionHelper.retrieveSearch(SESSION.SUPPLIER_SEARCH);
			filter.selectedRecordId = id;
			sessionHelper.storeSearch(SESSION.SUPPLIER_SEARCH, filter);
			this.$router.push({ name: "Supplier-update" });
		},
		closeForm() {
			this.supplier = {};
			this.$bvModal.hide("supplier-delete");
		},
		deleteConfirmation(item) {
			this.supplier = { id: item.uuid, isActive: item.isActive };
			this.$bvModal.show("supplier-delete");
		},
		requestDelete() {
			let data = { uuid: this.supplier.id, isActive: this.supplier.isActive == 0 ? 1 : 0 };
			this.updateSupplierStatus({ data, filter: this.searchCriteria }).then((result) =>
				this.$nextTick(() => {
					if (result?.success) this.$bvModal.hide("supplier-delete");
				})
			);
		},
	},
};
</script>
